import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { fetchFxRates } from '../components/home/apiService';
import { useLanguage } from '../i18n/LanguageProvider';

// Display currency for prices and forecasts. Everything is stored in SEK/kWh;
// this converts at display time with the ECB reference rates (GET /api/prices/fx).
// Euro is shown in cents per kWh, as Finnish price sites do.

export type Currency = 'SEK' | 'EUR' | 'NOK' | 'DKK';
export const CURRENCIES: Currency[] = ['SEK', 'EUR', 'NOK', 'DKK'];

interface CurrencyContextValue {
  currency: Currency; // the one in effect (SEK until rates have loaded)
  selected: Currency; // the viewer's choice
  setCurrency: (c: Currency) => void;
  convert: (sekPerKwh: number) => number;
  unit: string; // e.g. "kr/kWh", "c/kWh"
  format: (sekPerKwh: number) => string; // value + short symbol, e.g. "1.23 kr"
  ratesDate: string | null;
}

const CurrencyContext = createContext<CurrencyContextValue | undefined>(undefined);

const readStored = (): Currency => {
  try {
    const v = localStorage.getItem('currency');
    return CURRENCIES.includes(v as Currency) ? (v as Currency) : 'SEK';
  } catch {
    return 'SEK';
  }
};

export const CurrencyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useLanguage();
  const [selected, setSelected] = useState<Currency>(readStored);
  const [rates, setRates] = useState<{ date: string; sekPer: Record<string, number> } | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchFxRates()
      .then((r) => { if (!cancelled) setRates(r); })
      .catch((e) => console.error('[currency] rates unavailable, showing SEK', e));
    return () => { cancelled = true; };
  }, []);

  const setCurrency = useCallback((c: Currency) => {
    setSelected(c);
    try { localStorage.setItem('currency', c); } catch {}
  }, []);

  const value = useMemo<CurrencyContextValue>(() => {
    const currency: Currency = selected !== 'SEK' && rates?.sekPer[selected] ? selected : 'SEK';
    const scale = currency === 'SEK' ? 1 : (currency === 'EUR' ? 100 : 1) / rates!.sekPer[currency];
    const convert = (v: number) => v * scale;
    const unit = currency === 'SEK' ? t('current.unit') : currency === 'EUR' ? 'c/kWh' : `${currency}/kWh`;
    const symbol = currency === 'SEK' ? 'kr' : currency === 'EUR' ? 'c' : currency;
    const decimals = currency === 'EUR' ? 1 : 2;
    return {
      currency,
      selected,
      setCurrency,
      convert,
      unit,
      format: (v: number) => `${convert(v).toFixed(decimals)} ${symbol}`,
      ratesDate: rates?.date ?? null,
    };
  }, [selected, rates, setCurrency, t]);

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
};

export const useCurrency = () => {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error('useCurrency must be used within CurrencyProvider');
  return ctx;
};
